import { useSyncExternalStore } from 'react';
import { retryAfterSeconds } from '@kidova/api-client';

// §10.3 limits each parent to 60 requests per minute; without a Retry-After the window is a minute.
const FALLBACK_WAIT_S = 60;

type Listener = (until: number | null) => void;

/**
 * Remembers a 429 from the API and holds further reads until its Retry-After has passed, so the
 * dashboard waits once instead of spending the next minute's budget on requests that would be
 * refused too. Writes are not held: the parent is waiting on them and sees the error instead.
 */
export function createRateGate(
  now: () => number = Date.now,
  sleep: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
) {
  let until = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<Listener>();
  const emit = (value: number | null) => {
    for (const listener of listeners) listener(value);
  };
  return {
    /** Epoch ms until which reads wait, or null when the API is not limiting this parent. */
    limitedUntil(): number | null {
      return until > now() ? until : null;
    },
    observe(response: Response): void {
      if (response.status !== 429) return;
      const wait = retryAfterSeconds(response.headers.get('retry-after')) ?? FALLBACK_WAIT_S;
      until = Math.max(until, now() + wait * 1000);
      emit(until);
      clearTimeout(timer);
      timer = setTimeout(
        () => {
          emit(null);
        },
        Math.max(0, until - now()),
      );
    },
    async wait(): Promise<void> {
      const left = until - now();
      if (left > 0) await sleep(left);
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export type RateGate = ReturnType<typeof createRateGate>;

/** Whether reads are being held for a 429 right now. */
export function useRateLimited(gate: RateGate): boolean {
  return useSyncExternalStore(
    (onChange) => gate.subscribe(onChange),
    () => gate.limitedUntil() !== null,
    () => false,
  );
}
